import { useMemo } from 'react';
import {
  useQuery,
  useQueryClient,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ApiError } from '@/api/client.types';

import type { GradebookAssignment, StudentGradesState } from './gradebookTypes';
import {
  DEFAULT_GRADEBOOK_BATCH_SIZE,
  createGradebookBatchRanges,
  createInitialStudentGradesState,
  mergeGradebookStudents,
  useKuduGradebookService,
} from './gradebookService';

// The roster's ids are part of the key: the grades are fetched by position in
// the roster, so a roster that changed (students joined or left, or the first
// answer after a cached empty one) needs its own fetch. Without them a tab
// opened before the students joined kept its empty grades (issue #91, GRD-01).
export const studentGradesQueryKey = (
  courseId: string,
  componentIds: string[],
  rosterIds: string[] = [],
) =>
  [
    'gradebook',
    'student-grades',
    courseId,
    componentIds.join(',') || 'no-components',
    rosterIds,
  ] as const;

const NO_ROSTER_IDS: string[] = [];

export function studentGradesQueryKeyPrefix(courseId: string) {
  return ['gradebook', 'student-grades', courseId] as const;
}

interface UseStudentGradesOptions {
  assignments: GradebookAssignment[];
  batchSize?: number;
  componentIds: string[];
  courseId: string | undefined;
  enabled?: boolean;
  rosterIds?: string[];
  studentCount: number;
}

export function useStudentGrades({
  assignments,
  batchSize = DEFAULT_GRADEBOOK_BATCH_SIZE,
  componentIds,
  courseId,
  enabled = true,
  rosterIds = NO_ROSTER_IDS,
  studentCount,
}: UseStudentGradesOptions): UseQueryResult<StudentGradesState, ApiError> {
  const queryClient = useQueryClient();
  const service = useKuduGradebookService();
  const queryKey = useMemo(
    () => studentGradesQueryKey(courseId ?? 'missing-course', componentIds, rosterIds),
    [componentIds, courseId, rosterIds],
  );

  return useQuery<StudentGradesState, ApiError>({
    enabled: enabled && Boolean(courseId) && Boolean(service),
    placeholderData: () =>
      (queryClient.getQueryData(queryKey) as StudentGradesState | undefined) ??
      createInitialStudentGradesState(studentCount, batchSize),
    queryFn: async ({ signal }) => {
      if (!courseId || !service) {
        const error = new Error('Gradebook service is unavailable.') as ApiError;
        error.kind = 'data';
        throw error;
      }

      const ranges = createGradebookBatchRanges(studentCount, batchSize);
      // The grades the page has stay on it until the new ones replace them.
      // Every save, full credit and removal fetches the grades again, and the
      // fetch used to empty them first: a student's page lost its student
      // until the new grades came (issue #91, GRD-05).
      const shown =
        (queryClient.getQueryData(queryKey) as StudentGradesState | undefined)?.students ?? [];
      let fetched: StudentGradesState['students'] = [];
      const progress = (loaded: number): StudentGradesState => {
        const isComplete = loaded >= studentCount;

        return {
          batchSize,
          isComplete,
          loaded,
          students: isComplete ? fetched : mergeGradebookStudents(shown, fetched),
          total: studentCount,
        };
      };
      let state = progress(0);

      queryClient.setQueryData(queryKey, state);

      for (const range of ranges) {
        if (signal.aborted) {
          return state;
        }

        const nextStudents = await service.getStudentGrades(
          courseId,
          range.startIndex,
          range.endIndex,
          componentIds,
          assignments,
        );

        fetched = mergeGradebookStudents(fetched, nextStudents);
        state = progress(Math.min(studentCount, range.endIndex));
        queryClient.setQueryData(queryKey, state);
      }

      return state;
    },
    queryKey,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  } satisfies UseQueryOptions<StudentGradesState, ApiError>);
}
